import { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { faChild } from '@fortawesome/pro-solid-svg-icons/faChild';
import { faPaw } from '@fortawesome/pro-solid-svg-icons/faPaw';
import { faPeople } from '@fortawesome/pro-solid-svg-icons/faPeople';
import { faHouseHeart } from '@fortawesome/pro-solid-svg-icons/faHouseHeart';

import { outlinedButtonClasses } from 'us-react-components/dist/Button/Button';
import { bookingVerticalType } from 'us-react-components/dist/constants/Booking';
import { RectangleIconButton } from 'us-react-components/dist/RectangleIconButton/RectangleIconButton';
import { ParentSignupVerticalType, ParentSignupVerticalTypeId, ParentSignupVerticalTypeService } from 'us-web-services';
import { NuxContainer } from 'us-react-components/dist/NuxContainer/NuxContainer';
import { useDependencies } from '../../../dependencies';
import { Routes } from '../../../Routes';

const ACTIVE_CLASSES = 'text-sapphire border-sapphire ' + outlinedButtonClasses;

export const Vertical = observer((): JSX.Element => {
  const {
    stores: { familyRegistration, parent },
    services: { tracker }
  } = useDependencies();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const deletePreviousUserVerticalTypes = async (): Promise<void> => {
    try {
      if (parent.parent?.signupVerticalTypes) {
        const promises = parent.parent.signupVerticalTypes.map(async (type: ParentSignupVerticalType) => {
          if (type.id) {
            try {
              await ParentSignupVerticalTypeService.delete(type.id);
            } catch {
              // Suppress error removing previous vertical type (likely just out of sync with DB)
            }
          }
        });

        await Promise.all(promises);
      }

      parent.updateParent({
        ...parent.parent,
        signupVerticalTypes: [],
      });
    } catch {
      toast.error(t('notifications.genericError'));
    }
  }

  const createUserVerticalType = async (signupVerticalTypeId: ParentSignupVerticalTypeId): Promise<void> => {
    const verticalType = {
      parentId: familyRegistration.profile.id,
      signupVerticalType: {
        id: signupVerticalTypeId,
      }
    };

    try {
      const addedVertical = (await ParentSignupVerticalTypeService.create(verticalType)).data;
      parent.updateParent({
        ...parent.parent,
        signupVerticalTypes: [
          ...parent.parent.signupVerticalTypes || [],
          addedVertical.data,
        ]
      });
    } catch {
      toast.error(t('notifications.genericError'));
    }
  }

  familyRegistration.setCurrentRoute(Routes.parent.registration.vertical);

  const handleVerticalSelection = async (verticalTypeId: ParentSignupVerticalTypeId): Promise<void> => {
    const trackingProps = {
      elementId: ParentSignupVerticalTypeId[verticalTypeId],
      elementName: 'vertical-button',
    };
    tracker.track('clicked-button', trackingProps);

    await deletePreviousUserVerticalTypes();

    if (verticalTypeId !== ParentSignupVerticalTypeId.CHILD_CARE) {
      familyRegistration.resetDependents();
      familyRegistration.setIncludes([]);
      familyRegistration.setSweeteners([]);
    }

    if (verticalTypeId === ParentSignupVerticalTypeId.PET_CARE_CAT) {
      familyRegistration.setLookingFor(bookingVerticalType.PET_CARE);
    } else if (verticalTypeId === ParentSignupVerticalTypeId.CHILD_CARE) {
      familyRegistration.setLookingFor(bookingVerticalType.CHILD_CARE);
      familyRegistration.setSignupVerticalTypeId(ParentSignupVerticalTypeId.CHILD_CARE);
      createUserVerticalType(ParentSignupVerticalTypeId.CHILD_CARE);
    } else if (verticalTypeId === ParentSignupVerticalTypeId.SENIOR_CARE) {
      familyRegistration.setLookingFor(bookingVerticalType.SENIOR_CARE);
      familyRegistration.setSignupVerticalTypeId(ParentSignupVerticalTypeId.SENIOR_CARE);
      createUserVerticalType(ParentSignupVerticalTypeId.SENIOR_CARE);
    } else if (verticalTypeId === ParentSignupVerticalTypeId.HOUSEHOLD_SERVICES) {
      familyRegistration.setLookingFor(bookingVerticalType.HOUSEHOLD_SERVICES);
      familyRegistration.setSignupVerticalTypeId(ParentSignupVerticalTypeId.HOUSEHOLD_SERVICES);
      createUserVerticalType(ParentSignupVerticalTypeId.HOUSEHOLD_SERVICES);
    }
  }

  useEffect(() => {
    if (familyRegistration.lookingForId) {
      document.getElementById(`lookingFor-${familyRegistration.lookingForId}`)?.focus();
    }
  }, []);

  return (
    <NuxContainer
      familyRegistration={familyRegistration}
      navigate={navigate}
      tracker={tracker}
      title={t('pages.nux.family.vertical.title')} hideContinue>
      <div className='flex flex-col pt-10'>
        <div className='flex flex-row'>
          <div className='pr-1 sm:pr-2 md:pr-4 pb-2 md:pb-4'>
            <RectangleIconButton
              id={`lookingFor-${bookingVerticalType.CHILD_CARE}`}
              title={t('common.verticals.childcare')}
              onClick={async (): Promise<void> => {
                await handleVerticalSelection(ParentSignupVerticalTypeId.CHILD_CARE);
                navigate(Routes.parent.registration.frequency);
              }}
              icon={faChild}
              className={familyRegistration.lookingForId === bookingVerticalType.CHILD_CARE ? ACTIVE_CLASSES : ''}
            />
          </div>
          <div className='pl-1 sm:pl-2 md:pl-4 pb-2 md:pb-4'>
            <RectangleIconButton
              id={`lookingFor-${bookingVerticalType.PET_CARE}`}
              title={t('common.verticals.petCare')}
              onClick={async (): Promise<void> => {
                await handleVerticalSelection(ParentSignupVerticalTypeId.PET_CARE_CAT);
                navigate(Routes.parent.registration.petType);
              }}
              icon={faPaw}
              className={familyRegistration.lookingForId === bookingVerticalType.PET_CARE ? ACTIVE_CLASSES : ''}
            />
          </div>
        </div>
        <div className='flex flex-row'>
          <div className='pr-1 sm:pr-2 md:pr-4 pt-2'>
            <RectangleIconButton
              id={`lookingFor-${bookingVerticalType.SENIOR_CARE}`}
              title={t('common.verticals.seniorCare')}
              subtitle={t('pages.nux.family.vertical.rectangleButtons.seniorCareSubtitle')}
              onClick={async (): Promise<void> => {
                await handleVerticalSelection(ParentSignupVerticalTypeId.SENIOR_CARE);
                navigate(Routes.parent.registration.frequency);
              }}
              icon={faPeople}
              className={familyRegistration.lookingForId === bookingVerticalType.SENIOR_CARE ? ACTIVE_CLASSES : ''}
            />
          </div>
          <div className='pl-1 sm:pl-2 md:pl-4 pt-2'>
            <RectangleIconButton
              id={`lookingFor-${bookingVerticalType.HOUSEHOLD_SERVICES}`}
              title={t('common.verticals.householdServices')}
              subtitle={t('pages.nux.family.vertical.rectangleButtons.householdServicesSubtitle')}
              onClick={async (): Promise<void> => {
                await handleVerticalSelection(ParentSignupVerticalTypeId.HOUSEHOLD_SERVICES);
                navigate(Routes.parent.registration.frequency);
              }}
              icon={faHouseHeart}
              className={familyRegistration.lookingForId === bookingVerticalType.HOUSEHOLD_SERVICES ? ACTIVE_CLASSES : ''}
            />
          </div>
        </div>
      </div>
    </NuxContainer>
  )
});
